import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { summarizeAudio, checkTaskStatus } from '../../services/audioApi'; // Ensure correct import
import { 
    FileAudio, Download, Loader2, AlertCircle, 
    CheckCircle, Copy, Check, XCircle, Mic 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

// --- Sub-Components (Prompts) - Reusing logic ---
const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

// --- Main Component ---

const AudioSummarizer = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // UI & State
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState(null);
    
    // Status State
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    
    // Result State
    const [result, setResult] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    
    // Refs
    const pollingIntervalRef = useRef(null);

    // Cleanup audio preview URL on unmount
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [previewUrl]);

    // Polling Effect for task status
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        
                        const payload = data.result;

                        if (payload.new_credits !== undefined) {
                            setCredits(payload.new_credits);
                        }

                        if (payload.result) {
                            setResult({
                                text: payload.result.summary_text,
                                downloadUrl: payload.result.download_url
                            });
                            
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            // Handle direct structure if payload is the result itself
                            setResult({
                                text: payload.summary_text,
                                downloadUrl: payload.download_url
                            });
                            setProgressValue(100);
                            setIsProcessing(false);
                        }

                    } else if (data.state === 'FAILURE' || data.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('audioSummarizer.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        // Progress Updates
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    console.error("Polling error", err);
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
        
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [taskId, isProcessing, t, setCredits]);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            // Max 25MB check
            if (selected.size > 25 * 1024 * 1024) {
                setError(t('audioSummarizer.errors.tooLarge'));
                return;
            }
            
            setFile(selected);
            setFileName(selected.name);
            
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(selected));
            
            setError(null);
            setResult(null);
            setProgressValue(0);
        }
    };

    const handleClear = () => {
        setFile(null);
        setFileName('');
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setError(null);
        setResult(null);
        setProgressValue(0);
        
        const fileInput = document.getElementById('audio-upload');
        if (fileInput) fileInput.value = '';
        
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
    };

    const handleProcess = async () => {
        if (!file) {
            setError(t('common.errorNoFile'));
            return;
        }
        
        setIsProcessing(true);
        setProgressValue(5);
        setError(null);
        setResult(null);
        
        try {
            const response = await summarizeAudio(file);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message || t('audioSummarizer.errors.generic'));
            setIsProcessing(false);
        }
    };

    const handleCopy = () => {
        if (result?.text) {
            navigator.clipboard.writeText(result.text);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const getProgressLabel = (step) => {
        const map = {
            'downloading': t('audioSummarizer.steps.downloading'),
            'transcribing': t('audioSummarizer.steps.transcribing'),
            'summarizing': t('audioSummarizer.steps.summarizing'),
            'generating_file': t('audioSummarizer.steps.generating_file')
        };
        return map[step] || t('audioSummarizer.steps.processing');
    };

    // --- UI Components ---
    
    const UploadView = () => {
        return (
            <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-semibold mb-2">{t('audioSummarizer.title')}</h2>
                <p className="mt-2 text-brand-dark">{t('audioSummarizer.description')}</p>
                
                <div className="mt-8">
                    <label htmlFor="audio-upload" className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('audioSummarizer.uploadLabel')}
                    </label>
                    <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                        <Mic className="h-8 w-8 text-slate-500" />
                        <label htmlFor="audio-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer">
                            {t('audioSummarizer.selectFile')}
                            <input 
                                id="audio-upload" 
                                name="audio-upload" 
                                type="file" 
                                className="sr-only" 
                                onChange={handleFileChange} 
                                accept=".mp3,.wav,.ogg,.m4a" 
                            />
                        </label>
                        {fileName && (
                            <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                                <span className="truncate max-w-[200px] md:max-w-xs">{fileName}</span>
                                <button onClick={handleClear} title={t('audioSummarizer.clearFile')}>
                                    <XCircle className="h-5 w-5 text-red-500 hover:text-red-700" />
                                </button>
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{t('audioSummarizer.uploadHint')}</p>
                    
                    {error && (
                        <p className="mt-4 text-red-500 flex items-center">
                            <AlertCircle size={16} className="mr-2"/>
                            {error}
                        </p>
                    )}
                </div>

                {/* Audio Preview */}
                {previewUrl && (
                    <div className="mt-6 p-4 bg-slate-100 rounded-lg flex items-center justify-center">
                        <audio 
                            src={previewUrl} 
                            controls 
                            className="w-full"
                        />
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center space-x-4 mt-6">
                    <button
                        onClick={handleProcess}
                        disabled={!file || isProcessing}
                        className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                    >
                        {isProcessing ? (
                            <>
                                <Loader2 className="animate-spin mr-2" size={16} />
                                {t('audioSummarizer.steps.processing')}
                            </>
                        ) : (
                            t('audioSummarizer.buttons.summarize')
                        )}
                    </button>
                </div>

                {/* Progress Indicator */}
                {isProcessing && (
                    <div className="mt-6">
                        <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                            <span>{getProgressLabel(progressStep)}</span>
                            <span>{progressValue}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                            <div 
                                className="bg-brand-orange h-2.5 rounded-full transition-all duration-500 ease-out" 
                                style={{ width: `${progressValue}%` }}
                            ></div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2 text-center">
                            {t('audioSummarizer.waitMessage')}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const ResultView = () => {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-6 flex items-center text-green-800">
                    <CheckCircle className="mr-2" />
                    {t('audioSummarizer.success')}
                </div>

                <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
                    <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center flex-wrap gap-3">
                        <h4 className="font-semibold text-lg text-brand-dark">
                            {t('audioSummarizer.resultTitle')}
                        </h4>
                        
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCopy}
                                className="p-2 text-slate-600 hover:text-brand-orange hover:bg-slate-200 rounded-md transition"
                                title={t('audioSummarizer.copyText')}
                            >
                                {isCopied ? (
                                    <Check size={20} className="text-green-600" />
                                ) : (
                                    <Copy size={20} />
                                )}
                            </button>

                            {result.downloadUrl && (
                                <a 
                                    href={result.downloadUrl} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-md text-sm hover:bg-brand-orange transition"
                                >
                                    <Download size={16} />
                                    {t('audioSummarizer.buttons.downloadDocx')}
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="p-6 text-brand-dark">
                        <ReactMarkdown>
                            {result.text}
                        </ReactMarkdown>
                    </div>
                </div>
                
                <button 
                    onClick={handleClear}
                    className="mt-6 text-slate-600 hover:text-brand-orange underline block w-full text-center"
                >
                    {t('audioSummarizer.clearFile')}
                </button>
            </div>
        );
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : result ? (
                <ResultView />
            ) : (
                <UploadView />
            )}
        </div>
    );
};

export default AudioSummarizer;
